import { useQuery } from '@tanstack/react-query'
import { useMemo, useRef } from 'react'
import { z } from 'zod'
import { HorizontalScroller } from '@/components/commons/horizontal-scroller'
import { Tabs } from '@/components/commons/tabs'
import { ButtonLink } from '@/components/ui/button-link'
import { PlusIcon, SearchLgIcon } from '@/components/ui/icons'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput
} from '@/components/ui/input-group'
import { EventsList } from '@/features/marquee/components/events-list'
import { operatorQueryOptions } from '@/features/marquee/query-options'
import { isMarketManagerAllowed } from '@/features/marquee/utils'
import { debounce } from '@/helpers/utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { routes } from '@/routes'
import {
	type MarqueeEventState,
	type MarqueeTimeframe,
	sdkSchemas
} from '@/sdk'
import { useTranslation } from '@/translations/i18n'

const TIMEFRAME_TABS = [
	{ value: 'all', label: 'marquee:timeframe_all' },
	{ value: 'upcoming', label: 'marquee:timeframe_upcoming' },
	{ value: 'past', label: 'marquee:timeframe_past' }
] as const

/** Non-closed states, so "Upcoming" tracks the lifecycle if states are added. */
const NON_CLOSED_STATES = sdkSchemas.zMarqueeEventState.options.filter(
	(state) => state !== 'closed'
)

/** Tab → event states sent to the API (All sends none = every state). */
const STATUSES_BY_TAB: Record<
	MarqueeTimeframe,
	MarqueeEventState[] | undefined
> = {
	all: undefined,
	upcoming: NON_CLOSED_STATES,
	past: ['closed']
}

const MarqueeEventsSearchParamsSchema = z.object({
	timeframe: z.enum(['all', 'upcoming', 'past']).catch('all'),
	search: z.string().optional()
})

function MarqueeEvents() {
	const { t } = useTranslation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		MarqueeEventsSearchParamsSchema
	)

	const tabsViewportRef = useRef<HTMLDivElement>(null)

	// Setting an event up is creator work: a facilitator gets the tracker
	// without the way in.
	const { data: operator } = useQuery(operatorQueryOptions())
	const showCreateButton = isMarketManagerAllowed(operator, 'createEvent')

	const debouncedSearch = useMemo(
		() =>
			debounce((value: string) => {
				setSearchParams({ search: value || undefined })
			}, 300),
		[setSearchParams]
	)

	return (
		// Content flows and the document body scrolls at every breakpoint: the
		// title scrolls away, search + tabs stay sticky, and the list virtualizes
		// against the window scroll (same behaviour on mobile and desktop).
		<div className='flex flex-col'>
			<title>{t('marquee:page_title_events')}</title>
			{/* Title scrolls away with the page. */}
			<div className='flex items-center justify-between border-neutral-200 border-b l:px-10 px-4 pt-6 pb-6'>
				<h1 className='font-medium text-lg text-neutral-900'>
					{t('marquee:module_title')}
				</h1>
			</div>

			{/* Sticky so search and tabs stay pinned while the list scrolls
			    under them, at every breakpoint. */}
			<div className='sticky top-16 z-(--z-sticky-header) flex flex-col-reverse gap-4 bg-neutral-white pt-10 xl:flex-row xl:items-center xl:justify-between'>
				<HorizontalScroller viewportRef={tabsViewportRef}>
					<Tabs
						activeTab={searchParams.timeframe}
						className='w-max l:px-10 px-4 py-1'
						options={TIMEFRAME_TABS.map((tab) => ({
							label: t(tab.label),
							value: tab.value
						}))}
						scrollContainerRef={tabsViewportRef}
						setActiveTab={(timeframe) =>
							setSearchParams({
								timeframe:
									timeframe === 'all' ? undefined : timeframe
							})
						}
					/>
				</HorizontalScroller>

				<div className='flex items-center gap-4 l:px-10 px-4 xl:pl-0'>
					<InputGroup className='h-10 min-w-0 flex-1 rounded-full border-grey-600 xl:w-[345px] xl:flex-none'>
						<InputGroupAddon>
							<SearchLgIcon />
						</InputGroupAddon>
						<InputGroupInput
							defaultValue={searchParams.search ?? ''}
							onChange={(e) => debouncedSearch(e.target.value)}
							placeholder={t('marquee:search_placeholder')}
						/>
					</InputGroup>

					{showCreateButton && (
						<ButtonLink
							className='shrink-0 whitespace-nowrap'
							to={routes.MARQUEE_EVENT_NEW.handle.fullPath}
							variant='primary'
						>
							<PlusIcon />
							{t('marquee:create_new')}
						</ButtonLink>
					)}
				</div>
			</div>

			<EventsList
				search={searchParams.search}
				statuses={STATUSES_BY_TAB[searchParams.timeframe]}
				useWindowScroll
			/>
		</div>
	)
}

export { MarqueeEvents }
